import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Info } from 'lucide-react';

import { formatCoveragePercentage, useCoverageGap } from '@/features/shop-insurance';
import { formatCurrency } from '@/lib';
import {
  Alert,
  AlertDescription,
  Button,
  Progress,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@design-system';

export function CoverageGapCard() {
  const {
    coverageGap,
    coveredAmount,
    totalCost,
    coveragePercentage,
    hasCoverageCeiling,
    hasBelowAdlThreshold,
  } = useCoverageGap();

  const coverageTooltipContent = `${formatCurrency(coveredAmount)} / ${formatCurrency(totalCost)}`;

  return (
    <AnimatePresence>
      <motion.div
        key="gap-card"
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.25 }}
        style={{ overflow: 'hidden' }}
      >
        <div className="space-y-3 rounded-lg border bg-white p-4">
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <span className="text-primary-dark text-sm font-semibold">Max Modeled coverage</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Info className="size-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-56 text-xs">
                  The most any policy can cover of your projected care costs. Each product's limit
                  varies based on how and when its benefits apply.
                </TooltipContent>
              </Tooltip>
            </div>

            <div className="flex items-end justify-between text-sm">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-primary-dark cursor-help text-lg font-semibold underline decoration-dotted">
                    {formatCoveragePercentage(coveragePercentage)}% covered
                  </span>
                </TooltipTrigger>
                <TooltipContent>{coverageTooltipContent}</TooltipContent>
              </Tooltip>
              <div className="flex items-baseline gap-1">{formatCurrency(coverageGap)} gap</div>
            </div>
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-default">
                <Progress value={Math.min(100, coveragePercentage)} />
              </div>
            </TooltipTrigger>
            <TooltipContent className="text-xs">{coverageTooltipContent}</TooltipContent>
          </Tooltip>

          {hasCoverageCeiling && (
            <Alert variant="info">
              <AlertTriangle />
              <AlertDescription>
                Policy can&apos;t fully cover all care costs.{' '}
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto px-0 py-0 underline"
                    >
                      See why
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-64 text-xs">
                    Some of your projected LTC costs fall outside this policy's benefit eligibility
                    regardless of premium.{' '}
                    {hasBelowAdlThreshold
                      ? "This is because early care days where fewer than 2 ADLs are required are ineligible for benefits, and the policy's elimination period means the first several days of care are always out-of-pocket."
                      : "This is because the policy's elimination period means the first several days of care are always out-of-pocket."}
                  </TooltipContent>
                </Tooltip>
              </AlertDescription>
            </Alert>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
